import type { Config } from '../core/types';

// Handles DOM manipulation and behavior for the modal dialog.
export class ModalView {
  private static stylesInjected = false;
  private listenersAttached = false;
  private overlayElement!: HTMLElement;
  private contentDiv!: HTMLElement;
  private headerTitle!: HTMLElement;
  private isVisible = false;
  private onCloseRequested?: () => void;

  constructor(private config: Config) {
    this.createModalDOM();
  }

  setOnCloseRequested(callback: () => void): void {
    this.onCloseRequested = callback;
  }

  show(iframe: HTMLIFrameElement, headerText: string): void {
    this.headerTitle.textContent = headerText;
    
    // Only update iframe if different (prevents unnecessary DOM manipulation)
    const currentIframe = this.contentDiv.querySelector('iframe');
    if (currentIframe !== iframe) {
      this.contentDiv.innerHTML = '';
      this.contentDiv.appendChild(iframe);
    }
    
    this.overlayElement.classList.add('selectfi-modal-overlay--open');
    document.body.style.overflow = 'hidden';
    this.isVisible = true;
  }

  hide(): void {
    this.overlayElement.classList.remove('selectfi-modal-overlay--open');
    document.body.style.overflow = '';
    this.isVisible = false;
  }

  isOpen(): boolean {
    return this.isVisible;
  }

  private createModalDOM(): void {
    this.injectStyles();

    this.overlayElement = document.createElement('div');
    this.overlayElement.className = 'selectfi-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'selectfi-modal';

    const header = this.createHeader();
    this.contentDiv = document.createElement('div');
    this.contentDiv.className = 'selectfi-modal__content';

    modal.appendChild(header);
    modal.appendChild(this.contentDiv);
    this.overlayElement.appendChild(modal);
    document.body.appendChild(this.overlayElement);

    this.attachEventListeners();
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('header');
    header.className = 'selectfi-modal__header';

    const logoContainer = document.createElement('div');
    logoContainer.className = 'selectfi-modal__dealer-logo';
    const logo = document.createElement('img');
    logo.src = this.config.images.headerLogoUrl;
    logo.alt = 'Dealer Logo';
    logoContainer.appendChild(logo);

    this.headerTitle = document.createElement('h2');
    this.headerTitle.className = 'selectfi-modal__title';

    const closeButton = document.createElement('button');
    closeButton.className = 'selectfi-modal__close';
    closeButton.innerHTML = '&times;';
    closeButton.setAttribute('aria-label', 'Close modal');
    closeButton.addEventListener('click', () => this.requestClose());

    header.appendChild(logoContainer);
    header.appendChild(this.headerTitle);
    header.appendChild(closeButton);

    return header;
  }

  private attachEventListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;

    this.overlayElement.addEventListener('click', (event) => {
      if (event.target === this.overlayElement) {
        this.requestClose();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isVisible) {
        this.requestClose();
      }
    });
  }

  private requestClose(): void {
    if (this.onCloseRequested) {
      this.onCloseRequested();
    }
  }

  private injectStyles(): void {
    if (ModalView.stylesInjected) return;

    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-selectfi', 'modal');
    styleElement.textContent = `
      .selectfi-modal-overlay {
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 99999;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s ease, visibility 0.2s ease;
      }

      .selectfi-modal-overlay--open {
        opacity: 1;
        visibility: visible;
      }

      .selectfi-modal {
        background: #fff;
        border-radius: 8px;
        width: 90%;
        max-width: 900px;
        height: 90%;
        max-height: 1000px;
        display: flex;
        flex-direction: column;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }

      .selectfi-modal__header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid #e5e5e5;
        flex-shrink: 0;
      }

      .selectfi-modal__dealer-logo {
        flex: 0 0 auto;
        max-width: 120px;
        z-index: 1;
      }

      .selectfi-modal__dealer-logo img {
        max-height: 40px;
        width: auto;
        display: block;
      }

      .selectfi-modal__title {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        text-align: center;
        font-size: 18px;
        font-weight: 600;
        color: #1a365d;
        margin: 0;
        padding: 0 16px;
        max-width: calc(100% - 280px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selectfi-modal__close {
        flex: 0 0 auto;
        background: none;
        border: none;
        font-size: 28px;
        line-height: 1;
        color: #666;
        cursor: pointer;
        padding: 4px 8px;
        transition: color 0.15s ease;
        z-index: 1;
      }

      .selectfi-modal__close:hover {
        color: #333;
      }

      .selectfi-modal__content {
        flex: 1;
        overflow: hidden;
      }

      .selectfi-modal__footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 10px 16px;
        border-top: 1px solid #e5e5e5;
        background: #f9f9f9;
        flex-shrink: 0;
      }

      .selectfi-modal__footer span {
        font-size: 13px;
        color: #666;
      }

      .selectfi-modal__footer-logo {
        height: 20px;
        width: auto;
      }

      @media (max-width: 600px) {
        .selectfi-modal {
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          border-radius: 0;
        }

        .selectfi-modal__title {
          font-size: 14px;
          max-width: calc(100% - 200px);
        }

        .selectfi-modal__dealer-logo img {
          max-height: 30px;
        }
      }
    `;

    document.head.appendChild(styleElement);
    ModalView.stylesInjected = true;
  }
}
